"use client";

import Image from "next/image";
import Link from "@/components/ui/Link";
import LiveBlink from "@/components/ui/LiveBlink";
import { isLiveBlogValue } from "@/lib/liveBlog";
import { MessageIcon } from "../ui/message-icon";
import { BOOKMARK_CATEGORIES } from "@/lib/api/public-client";
import { FALLBACK_IMAGE } from '@/lib/constants';
import { getImageUrl, getTimeAgo, getFocusPointStyle, toFieldString, ImageMetadata, BLUR_PLACEHOLDER } from '@/lib/componentutills';

type FieldConfig = {
  hidden?: boolean;
  value?: string | boolean;
  customClass?: string;
};

type Props = {
  category?: FieldConfig;
  title?: FieldConfig;
  excerpt?: FieldConfig;
  timeAgo?: FieldConfig;
  imageSrc?: FieldConfig;
  slug?: string;
  priority?: boolean;
  imageMetadata?: ImageMetadata;
  isFirstComponent?: boolean;
  templatetype?: FieldConfig;
  isliveblog?: FieldConfig;
};

export default function HorizontalBigImageNews({
  title = { hidden: false, value: "'கடின கதோரி அண்டகடஹம்' திரைப்பட விமர்சனம்: வெகு தொனலியில் இல்லாத கடந்த காலத்திலிருந்து உணர்ச்சியை", customClass: "" },
  excerpt = { hidden: false, value: "நாட்டின் நிலக்கரி உற்பத்தி 2023 ஏப்ரலில் 8.5 சதவீதம் அதிகரித்து 7.314 கோடி டன்னாக", customClass: "" },
  timeAgo = { hidden: false, value: "15 min ago", customClass: "" },
  imageSrc = { hidden: false, value: FALLBACK_IMAGE, customClass: "" },
  slug,
  priority = false,
  imageMetadata,
  isFirstComponent = false,
  templatetype = { hidden: false, value: "", customClass: "" },
  isliveblog = { hidden: false, value: "", customClass: "" },
}: Props) {
  const titleValue = toFieldString(title?.value);
  const excerptValue = toFieldString(excerpt?.value);
  const imageSrcValue = getImageUrl(toFieldString(imageSrc?.value)) || FALLBACK_IMAGE;
  const timeAgoValue = getTimeAgo(toFieldString(timeAgo?.value));
  const isLiveBlog = isLiveBlogValue(templatetype?.value) || isLiveBlogValue(isliveblog?.value);

  const content = (
    <article className="p-4">
      <div className="flex flex-col md:flex-row gap-4">
        {/* IMAGE */}
        {!imageSrc?.hidden && (
          <div className={`relative shrink-0 overflow-hidden w-full aspect-[16/9] md:aspect-auto md:w-5/12 lg:w-2/5 md:h-[230px] ${imageSrc?.customClass || ""}`}>
            <Image
              src={imageSrcValue}
              alt={titleValue}
              fill
              className="object-cover"
              style={getFocusPointStyle(imageMetadata)}
              sizes="(max-width: 768px) 100vw, 40vw"
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : "auto"}
              priority={priority}
              placeholder="blur"
              blurDataURL={BLUR_PLACEHOLDER}
            />
          </div>
        )}

        {/* CONTENT */}
        <div className="flex flex-col justify-between w-full">
          {/* Top Content */}
          <div className="flex flex-col gap-3">
            {!title?.hidden && (
 <h3 className={`text-24-tamil-700 md:text-14-tamil-700 text-012061 font-medium leading-[1.4] ${title?.customClass || ""}`}>
                {isLiveBlog && (
                  <LiveBlink />
                )}
                {titleValue}
              </h3>
            )}

            {!excerpt?.hidden && (
              <p className={`text-14-tamil-400 md:text-12-tamil-400 text-4D4D4D leading-[1.6] line-clamp-2 ${excerpt?.customClass || ""}`}>
                {excerptValue}
              </p>
            )}
          </div>

          {/* Bottom Footer */}
          <div className="mt-3">
            <div className="flex items-center justify-between">
              {!timeAgo?.hidden && (
                <span className={`text-12-tamil-500 text-4D4D4D ${timeAgo?.customClass || ""}`}>{timeAgoValue}</span>
              )}
              <MessageIcon
                id="image-news-desc-vert-message-icon"
                slug={slug}
                category={BOOKMARK_CATEGORIES.NEWS}
                title={titleValue}
                thumbnailUrl={imageSrcValue}
                description={excerptValue}
                articleUrl={slug ? `/${slug}` : undefined}
              />
            </div>
          </div>
        </div>
      </div>
    </article>
  );

  if (slug) {
    return (
      <Link href={`/${slug}`} className="block  transition-colors cursor-pointer">
        {content}
      </Link>
    );
  }

  return content;
}
